// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';
import { normalizarBusca } from '@/nucleo/utilitarios';

import type { FiltroDeRecursos, RecursoAdministrativo } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados dos Recursos Administrativos do Portal do Credenciado.
 *
 * Mesmo desenho das demais telas do módulo: nenhum componente busca dado
 * direto, a chave de cache mora junto de quem a usa, e o `queryFn` resolve
 * `dados.mock.json` em vez de chamar `chamarApi('/credenciado/...')` — ver a
 * justificativa em `comum/mock.ts`.
 */
export const CHAVES_DE_RECURSOS = {
  recursos: (filtro: FiltroDeRecursos) => ['credenciado', 'recursos', filtro] as const,
};

const RECURSOS_MOCK = dadosMock.recursos as RecursoAdministrativo[];

function aplicarFiltro(
  itens: RecursoAdministrativo[],
  filtro: FiltroDeRecursos,
): RecursoAdministrativo[] {
  return itens.filter((recurso) => {
    if (filtro.status && recurso.status !== filtro.status) {
      return false;
    }

    if (filtro.protocoloDe && recurso.data_protocolo < filtro.protocoloDe) {
      return false;
    }

    if (filtro.protocoloAte && recurso.data_protocolo > filtro.protocoloAte) {
      return false;
    }

    if (filtro.busca) {
      const alvo = normalizarBusca(filtro.busca);
      const combinaPorReferencia = normalizarBusca(recurso.referencia).includes(alvo);
      const combinaPorNumero = normalizarBusca(recurso.numero_processo).includes(alvo);
      if (!combinaPorReferencia && !combinaPorNumero) {
        return false;
      }
    }

    return true;
  });
}

/**
 * A listagem de recursos, paginada no mesmo formato que a API real vai
 * devolver (`comum/contrato.ts`). O filtro acontece antes da paginação —
 * igual a um `WHERE` que roda antes do `LIMIT/OFFSET` no servidor.
 */
export function useRecursos(
  filtro: FiltroDeRecursos,
): UseQueryResult<Pagina<RecursoAdministrativo>, Error> {
  return useQuery<Pagina<RecursoAdministrativo>, Error>({
    queryKey: CHAVES_DE_RECURSOS.recursos(filtro),
    queryFn: () =>
      resolverMock(paginar(aplicarFiltro(RECURSOS_MOCK, filtro), filtro.pagina, filtro.tamanho)),
    placeholderData: (anterior) => anterior,
  });
}
